import type { FileType } from "@/api";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
    BookOpen,
    FileSearch2,
    FileSpreadsheet,
    FileText,
    Folder,
    type LucideIcon,
    Table as TableIcon,
} from "lucide-react";
import type { ReactNode } from "react";

export type LibraryItemTypeKey =
    | "folder"
    | "knowledge_base"
    | "document"
    | "scope"
    | "comparison";

interface LibraryItemTypeConfig {
    icon: LucideIcon;
    label: string;
    /** Tailwind classes applied to the icon when rendered standalone. */
    iconClassName: string;
    /** Tailwind classes applied to the Badge body (bg + text). */
    pillClassName: string;
    /** Extra icon props (e.g., `fill="currentColor"` for folders). */
    iconProps?: Record<string, unknown>;
}

const LIBRARY_ITEM_TYPES: Record<LibraryItemTypeKey, LibraryItemTypeConfig> = {
    folder: {
        icon: Folder,
        label: "Folder",
        iconClassName: "text-muted-foreground/70",
        pillClassName: "bg-muted/60 text-muted-foreground",
        iconProps: { fill: "currentColor" },
    },
    knowledge_base: {
        icon: BookOpen,
        label: "Knowledge Base",
        iconClassName: "text-primary/70",
        pillClassName: "bg-primary/10 text-primary",
    },
    document: {
        icon: FileText,
        label: "Document",
        iconClassName: "text-destructive/70",
        pillClassName: "bg-destructive/10 text-destructive",
    },
    scope: {
        icon: FileSearch2,
        label: "Scope",
        iconClassName: "text-blue-500/70 dark:text-blue-400/70",
        pillClassName:
            "bg-blue-500/10 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400",
    },
    comparison: {
        icon: TableIcon,
        label: "Comparison",
        iconClassName: "text-amber-500/70 dark:text-amber-400/70",
        pillClassName:
            "bg-amber-500/10 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400",
    },
};

/**
 * Render the lucide icon for a library item type with the canonical Tailwind
 * color + size. Trailing args (`_theme`, `_size`) are accepted for backward
 * compatibility with pre-migration call sites that still pass MUI's `theme`.
 * `_theme` is ignored; `_size` is honored if provided so the existing 12px
 * filter-option call site keeps its smaller glyph.
 */
export function getLibraryItemIcon(
    type: LibraryItemTypeKey,
    _theme?: unknown,
    _size?: number
): ReactNode {
    const config = LIBRARY_ITEM_TYPES[type];
    const Icon = config.icon;
    const size = _size ?? 18;
    return (
        <Icon
            size={size}
            strokeWidth={1.8}
            className={cn("shrink-0", config.iconClassName)}
            {...config.iconProps}
        />
    );
}

/**
 * Render a per-type pill (icon + label) as a shadcn `Badge`. The pre-migration
 * MUI `MetadataChip` palette is preserved semantically — same color intent,
 * expressed in Tailwind tokens. `_theme` accepted-and-ignored for backward
 * compatibility.
 */
export function getLibraryItemPill(
    type: LibraryItemTypeKey,
    _theme?: unknown
): ReactNode {
    const config = LIBRARY_ITEM_TYPES[type];
    const Icon = config.icon;
    return (
        <Badge
            className={cn(
                "gap-1 border-transparent px-2 py-0.5",
                config.pillClassName
            )}
        >
            <Icon
                size={12}
                strokeWidth={1.8}
                className="shrink-0"
                {...config.iconProps}
            />
            {config.label}
        </Badge>
    );
}

// Per-`file_type` document glyph. Distinct SHAPE per type (not color-only) so it
// reads at a glance in a dense list. The spreadsheet family (`xlsx`/`csv`) shares
// green — the shape (workbook vs table) tells them apart — while `pdf`/legacy-
// `null` keep the document red and share the PDF glyph (spreadsheet-positive).
const DOCUMENT_FILE_TYPE_ICONS = {
    xlsx: {
        icon: FileSpreadsheet,
        label: "Excel spreadsheet",
        className: "text-emerald-600/80 dark:text-emerald-400/80",
    },
    csv: {
        icon: TableIcon,
        label: "CSV spreadsheet",
        className: "text-emerald-600/80 dark:text-emerald-400/80",
    },
    pdf: {
        icon: FileText,
        label: "PDF document",
        className: "text-destructive/70",
    },
} as const;

/**
 * Render the document-type glyph for a persisted `file_type` with a SEMANTIC
 * accessible name — an outer `role="img"` + `aria-label`, never color-only — so
 * a spreadsheet is distinguishable from a PDF both at a glance and to assistive
 * tech: `xlsx` → spreadsheet glyph ("Excel spreadsheet"), `csv` → table glyph
 * ("CSV spreadsheet"), `pdf`/`null` → `FileText` ("PDF document").
 */
export function getDocumentFileTypeIcon(
    fileType: FileType | null | undefined
): ReactNode {
    const config =
        fileType === "xlsx" || fileType === "csv"
            ? DOCUMENT_FILE_TYPE_ICONS[fileType]
            : DOCUMENT_FILE_TYPE_ICONS.pdf;
    const Icon = config.icon;
    return (
        <span
            role="img"
            aria-label={config.label}
            className="inline-flex shrink-0"
        >
            <Icon
                size={18}
                strokeWidth={1.8}
                className={config.className}
                aria-hidden
            />
        </span>
    );
}

// The "Type" column names a document by its category, not the flat "Document".
// Two user-facing buckets: the spreadsheet family (`xlsx`/`csv` → green) and PDF
// (`pdf`/legacy-`null` → the document red — "what document is"). The Name-column
// glyph still distinguishes Excel from CSV by shape; this pill names the bucket.
const DOCUMENT_TYPE_PILLS = {
    spreadsheet: {
        icon: FileSpreadsheet,
        label: "Spreadsheet",
        pillClassName:
            "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
    },
    pdf: {
        icon: FileText,
        label: "PDF",
        // Matches the legacy "Document" pill (destructive) — a PDF keeps the
        // document tone; only the label narrows from "Document" to "PDF".
        pillClassName: "bg-destructive/10 text-destructive",
    },
} as const;

/**
 * Render the "Type" column pill for a document from its persisted `file_type`.
 * Spreadsheets (`xlsx`/`csv`) collapse into one green "Spreadsheet" bucket; PDFs
 * (and legacy `null`) keep the document red and read "PDF" (spreadsheet-positive
 * — an unknown/legacy type is treated as a PDF).
 */
export function getDocumentTypePill(
    fileType: FileType | null | undefined
): ReactNode {
    const config =
        fileType === "xlsx" || fileType === "csv"
            ? DOCUMENT_TYPE_PILLS.spreadsheet
            : DOCUMENT_TYPE_PILLS.pdf;
    const Icon = config.icon;
    return (
        <Badge
            className={cn(
                "gap-1 border-transparent px-2 py-0.5",
                config.pillClassName
            )}
        >
            <Icon
                size={12}
                strokeWidth={1.8}
                className="shrink-0"
                aria-hidden
            />
            {config.label}
        </Badge>
    );
}

export type LibraryTypeFilter = "all" | "documents" | "scopes" | "comparisons";

const VALID_TYPE_FILTERS: LibraryTypeFilter[] = [
    "all",
    "documents",
    "scopes",
    "comparisons",
];

const SCOPE_TYPE_FILTERS: LibraryTypeFilter[] = ["scopes", "comparisons"];

export function isValidTypeFilter(value: string): value is LibraryTypeFilter {
    return (VALID_TYPE_FILTERS as string[]).includes(value);
}

/** Resolve a `type_filter` param, rejecting the scope views for a scopes-off org. */
export function parseTypeFilter(
    value: string | null | undefined,
    scopesEnabled: boolean
): LibraryTypeFilter | null {
    if (!value || !isValidTypeFilter(value)) return null;
    if (!scopesEnabled && (SCOPE_TYPE_FILTERS as string[]).includes(value)) {
        return null;
    }
    return value;
}

export function getTypeFilterOptions(scopesEnabled: boolean) {
    const types: LibraryItemTypeKey[] = scopesEnabled
        ? ["document", "scope", "comparison"]
        : ["document"];
    return [
        { value: "all" as const, label: "All" },
        ...types.map((type) => ({
            value: `${type}s` as LibraryTypeFilter,
            label: LIBRARY_ITEM_TYPES[type].label + "s",
            icon: getLibraryItemIcon(type, undefined, 12),
        })),
    ];
}
